import {
  Bell,
  CalendarCheck,
  CalendarRangeIcon,
  CloudOff,
  Cpu,
  FileCode2,
  FileQuestion,
  Gauge,
  HelpCircle,
  LayoutGrid,
  LayoutList,
  LayoutTemplate,
  LineChart,
  LocateFixed,
  MapPinned,
  Mountain,
  Network,
  Phone,
  Power,
  School,
  UserCheck2,
  UtilityPole,
  Wind,
  Wrench,
  Zap,
} from 'lucide-react';

import { AtlasIcon } from '@crossnokaye/ux-animatics';
import { cn } from '@crossnokaye/ux-common';

import { AtlasRouteIds, LeftNavItemCategoryOptions } from '@/components/navigation/constants';
import { NavItemConfig } from '@/components/navigation/types';
import { SUPPORT_CONTACT_NUMBER } from '@/constants';
import { PRIVILEGES } from '@/hooks/access/access-table';

export const ZENDESK_SITE_DOCUMENTATION_URL =
  'https://crossnokaye.zendesk.com/hc/en-us/categories/16198600954907-Site-Documents';
export const ZENDESK_HELP_CENTER_URL = 'https://crossnokaye.zendesk.com';
// Fallback target used when the `launch_absorb_sso` endpoint can't be reached
// (or until the typescript-sdk regenerates with the new endpoint from
// ux-services PR 1192). Sends the user to the plain Absorb login screen.
export const ABSORB_LOGIN_FALLBACK_URL = 'https://crossnokaye.myabsorb.com/#/login';

/**
 * Used to uniformly provide props to icons
 */
const commonProps = {
  className: 'glyph glyph-sm',
  'aria-hidden': true,
};

/** Left Nav Items
 * Modifying this will change the left nav items!
 * Use this to configure the left nav items available to select
 */
export const routeOptions: NavItemConfig[] = [
  {
    id: AtlasRouteIds.ProcessView,
    label: 'Process',
    category: LeftNavItemCategoryOptions.Controls,
    accessRestricted: true,
    icon: <Network {...commonProps} />,
  },
  {
    id: AtlasRouteIds.System,
    label: 'System',
    category: LeftNavItemCategoryOptions.Controls,
    accessRestricted: true,
    icon: <LayoutList {...commonProps} />,
    path: AtlasRouteIds.Equipment,
  },
  {
    id: AtlasRouteIds.Map,
    label: 'Facility',
    category: LeftNavItemCategoryOptions.Controls,
    accessRestricted: true,
    icon: <MapPinned {...commonProps} />,
  },
  {
    id: AtlasRouteIds.Equipment,
    label: 'Equipment',
    category: LeftNavItemCategoryOptions.Controls,
    accessRestricted: true,
    icon: <LayoutGrid {...commonProps} />,
  },
  {
    id: AtlasRouteIds.Alarms,
    label: 'Alarms',
    category: LeftNavItemCategoryOptions.Controls,
    accessRestricted: true,
    icon: <Bell {...commonProps} />,
  },
  {
    id: AtlasRouteIds.Trends,
    label: 'Trends',
    category: LeftNavItemCategoryOptions.Controls,
    accessRestricted: true,
    icon: <LineChart {...commonProps} />,
  },

  {
    id: AtlasRouteIds.Shutdown,
    label: 'Building Shutdown',
    category: LeftNavItemCategoryOptions.Advanced,
    accessRestricted: true,
    icon: <Power {...commonProps} />,
  },
  {
    id: AtlasRouteIds.CompressorSequencer,
    label: 'Compressor Sequencing',
    category: LeftNavItemCategoryOptions.Advanced,
    accessRestricted: true,
    icon: <AtlasIcon symbol={'compressor'} size={'1rem'} />,
    path: AtlasRouteIds.Equipment,
  },
  {
    id: AtlasRouteIds.CondenserSequencer,
    label: 'Condenser Sequencing',
    category: LeftNavItemCategoryOptions.Advanced,
    accessRestricted: true,
    icon: <AtlasIcon symbol={'condenser'} size={'1rem'} />,
    path: AtlasRouteIds.Equipment,
  },
  {
    id: AtlasRouteIds.OfflineEscapeHatch,
    label: 'Work Offline',
    category: LeftNavItemCategoryOptions.Advanced,
    // Visibility gated by useLocalOnlineMode, not entitlements
    accessRestricted: false,
    icon: (
      <div className={cn('absolute flex items-center justify-center rounded-full bg-neutral-800')}>
        <CloudOff {...commonProps} />
      </div>
    ),
  },
  {
    id: AtlasRouteIds.IOStatus,
    label: 'I/O Status',
    category: LeftNavItemCategoryOptions.Tools,
    accessRestricted: true,
    icon: <Cpu {...commonProps} />,
  },
  {
    id: AtlasRouteIds.Netkit,
    label: 'Netkit',
    category: LeftNavItemCategoryOptions.Tools,
    accessRestricted: true,
    icon: <Network {...commonProps} />,
  },
  {
    id: AtlasRouteIds.SensorCalibration,
    label: 'Sensor Calibration',
    category: LeftNavItemCategoryOptions.Tools,
    accessRestricted: true,
    icon: <LocateFixed {...commonProps} />,
  },
  {
    id: AtlasRouteIds.Blast,
    label: 'Blast',
    category: LeftNavItemCategoryOptions.Apps,
    accessRestricted: true,
    icon: <CalendarCheck {...commonProps} />,
  },
  {
    id: AtlasRouteIds.EnergyAI,
    label: 'Energy AI',
    category: LeftNavItemCategoryOptions.Apps,
    accessRestricted: true,
    icon: <Zap {...commonProps} />,
  },
  {
    id: AtlasRouteIds.PredictiveDryingControl,
    label: 'Predictive Drying Control',
    collapsedLabel: 'Drying',
    category: LeftNavItemCategoryOptions.Apps,
    accessRestricted: false,
    flagRestricted: true,
    icon: <Wind {...commonProps} />,
  },
  {
    id: AtlasRouteIds.FacilityPerformance,
    label: 'Facility Performance',
    category: LeftNavItemCategoryOptions.Performance,
    accessRestricted: false,
    icon: <Gauge {...commonProps} />,
  },
  {
    id: AtlasRouteIds.DemandEvents,
    label: 'Demand Events',
    category: LeftNavItemCategoryOptions.Performance,
    accessRestricted: false,
    icon: <UtilityPole {...commonProps} />,
  },
  {
    id: AtlasRouteIds.CoincidentPeaks,
    label: 'Coincident Peak Events',
    category: LeftNavItemCategoryOptions.Performance,
    accessRestricted: true,
    icon: <Mountain {...commonProps} />,
  },
  {
    id: AtlasRouteIds.DemandResponseEvents,
    label: 'Demand Response Events',
    category: LeftNavItemCategoryOptions.Performance,
    accessRestricted: false,
    flagRestricted: true,
    icon: <CalendarRangeIcon {...commonProps} />,
  },
  {
    id: AtlasRouteIds.DemandResponseAdmin,
    label: 'Demand Response Admin',
    category: LeftNavItemCategoryOptions.Admin,
    accessRestricted: false,
    privilegeRestricted: PRIVILEGES.readDemandResponse,
    icon: <CalendarRangeIcon {...commonProps} />,
  },
  {
    id: AtlasRouteIds.FacilityConfig,
    label: 'Facility Configuration',
    collapsedLabel: 'Config',
    category: LeftNavItemCategoryOptions.Admin,
    accessRestricted: true,
    icon: <Wrench {...commonProps} />,
  },
  {
    id: AtlasRouteIds.UxConfig,
    label: 'UX Config',
    category: LeftNavItemCategoryOptions.Admin,
    accessRestricted: true,
    flagRestricted: true,
    icon: <LayoutTemplate {...commonProps} />,
  },
  {
    id: AtlasRouteIds.Uxds,
    label: 'UXDS',
    category: LeftNavItemCategoryOptions.Admin,
    accessRestricted: false,
    flagRestricted: true,
    icon: <FileCode2 {...commonProps} />,
  },
  {
    id: AtlasRouteIds.UserManagement,
    label: 'User Management',
    category: LeftNavItemCategoryOptions.Admin,
    accessRestricted: true,
    icon: <UserCheck2 {...commonProps} />,
  },
  {
    id: AtlasRouteIds.HelpCenter,
    label: 'Help Center',
    category: LeftNavItemCategoryOptions.Support,
    accessRestricted: true,
    // Hidden in preauthenticated (agent-proxy / local online) mode via useLeftNavAccess
    icon: <HelpCircle {...commonProps} />,
  },
  {
    id: AtlasRouteIds.AtlasLearning,
    // Brand-new route that never existed in Darkwing, so there is no
    // access-control entitlement for it. It must NOT be accessRestricted or
    // useLeftNavAccess filters it out for everyone. Shown to all users.
    accessRestricted: false,
    label: 'ATLAS Learning',
    category: LeftNavItemCategoryOptions.Support,
    icon: <School {...commonProps} />,
  },
  {
    id: AtlasRouteIds.SiteNavigation,
    label: 'Site Documents',
    category: LeftNavItemCategoryOptions.Support,
    accessRestricted: true,
    // Hidden in preauthenticated (agent-proxy / local online) mode via useLeftNavAccess
    icon: <FileQuestion {...commonProps} />,
  },
  {
    id: AtlasRouteIds.Support,
    label: SUPPORT_CONTACT_NUMBER,
    category: LeftNavItemCategoryOptions.Support,
    accessRestricted: false,
    icon: <Phone {...commonProps} />,
  },
];

/**
 * Number of skeleton items to show in the collapsed nav skeletons
 * Controls + Apps
 */
export const NAV_SKELETON_COUNT = routeOptions.filter(
  (item) =>
    item.category === LeftNavItemCategoryOptions.Controls ||
    item.category === LeftNavItemCategoryOptions.Apps
).length;
